import { useMemo } from "react";
import { useQuery } from "react-query";

import { useLicenses } from "~/entity/Licenses/hooks/useLicenses";
import { fetchAllLicenseOrganizations } from "~/entity/Licenses/lib/fetchAllLicenseOrganizations";
import {
  ILicenseOrganizationRowApi,
  ILicensesOverviewParams,
} from "~/entity/Licenses/model/schema";
import { getAvailabilityColor } from "~/shared/lib/licenseAvailability";

import { ILicenseSummaryRow } from "./types";

export const mapOrganizationRow = (
  row: ILicenseOrganizationRowApi,
): ILicenseSummaryRow => {
  const isPartner = row.ownerType === "PARTNER";
  const name = row.name ?? "";

  return {
    id:
      row.dealerId != null
        ? `dealer-${row.dealerId}`
        : `org-${row.organizationId ?? name}`,
    dealerId: row.dealerId,
    organizationId: row.organizationId,
    organizationName: name,
    inn: row.inn ?? "",
    branchCode: row.branchCode ?? "",
    dealerName: isPartner ? "" : name,
    partnerName: isPartner ? name : "",
    city: row.city ?? "",
    region: row.region ?? "",
    country: row.country ?? "",
    federalDistrict: row.federalDistrict ?? "",
    availabilityColor: getAvailabilityColor(row.available, row.total),
    totalQuantity: row.total,
    assignedQuantity: row.assigned,
    usedQuantity: row.used,
    frozenQuantity: row.frozen,
    freeQuantity: row.available,
    expiredQuantity: row.expired,
  };
};

export const useLicenseOrganizations = (params?: ILicensesOverviewParams) => {
  const { queryKeys, getLicensesOrganizations } = useLicenses();

  const query = useQuery({
    queryKey: queryKeys.organizations(params),
    queryFn: () => getLicensesOrganizations(params),
  });

  const data = query.data?.data;
  const rows =
    data?.level === "organizations" ? data.rows.map(mapOrganizationRow) : [];

  return {
    ...query,
    rows,
    total: data?.level === "organizations" ? data.total : 0,
    statusTotals:
      data?.level === "organizations" ? data.statusTotals : undefined,
    pool: data?.level === "organizations" ? data.pool : undefined,
  };
};

export const getLicenseFilterValueOptions = (
  rows: ILicenseSummaryRow[],
  filterBy?: string,
) => {
  const fieldByFilter: Record<string, keyof ILicenseSummaryRow> = {
    dealer: "dealerName",
    partner: "partnerName",
    city: "city",
    region: "region",
    country: "country",
    federalDistrict: "federalDistrict",
  };

  const field = filterBy ? fieldByFilter[filterBy] : undefined;

  if (!field) {
    return [];
  }

  const values = rows
    .map((row) => String(row[field]))
    .filter(Boolean)
    .filter((value, index, list) => list.indexOf(value) === index)
    .sort((a, b) => a.localeCompare(b));

  return values.map((name) => ({ value: name, label: name }));
};

export const useLicenseFilterValueOptions = (filterBy?: string) => {
  const { queryKeys } = useLicenses();
  const query = useQuery({
    queryKey: queryKeys.organizations(undefined),
    queryFn: () => fetchAllLicenseOrganizations(),
    enabled: Boolean(filterBy),
  });

  return useMemo(
    () =>
      getLicenseFilterValueOptions(
        (query.data ?? []).map(mapOrganizationRow),
        filterBy,
      ),
    [query.data, filterBy],
  );
};
